/* eslint-disable no-case-declarations */
import { useCallback, useEffect, useState } from 'react';

export enum CronFrequency {
  Weekly = 'Weekly',
  Monthly = 'Monthly',
  Quarterly = 'Quarterly',
  Yearly = 'Yearly',
}

export enum CronDayOfWeek {
  Sunday = 'Sunday',
  Monday = 'Monday',
  Tuesday = 'Tuesday',
  Wednesday = 'Wednesday',
  Thursday = 'Thursday',
  Friday = 'Friday',
  Saturday = 'Saturday',
}
export enum AmPm {
  AM = 'AM',
  PM = 'PM',
}
export enum Timezone {
  UTC = 'UTC',
  EST = 'EST',
  EDT = 'EDT',
}

export const getTimezoneOffset = (timezone: Timezone): number => {
  return timezone === Timezone.EST ? 5 : timezone === Timezone.EDT ? 4 : 0;
};

const detectEasternTimezone = (): Timezone.EST | Timezone.EDT => {
  const now = new Date();
  const january = new Date(now.getFullYear(), 0, 1);
  const july = new Date(now.getFullYear(), 6, 1);
  const stdOffset = Math.max(january.getTimezoneOffset(), july.getTimezoneOffset());
  const isDST = now.getTimezoneOffset() < stdOffset;
  return isDST ? Timezone.EDT : Timezone.EST;
};
export const CRON_FREQUENCY_OPTIONS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));

const useCronBuilder = (initialState?: {
  frequency?: CronFrequency;
  selectedDays?: CronDayOfWeek[];
  selectedHour?: string;
  selectedMinute?: string;
  meridiem?: AmPm;
  ignoreTimezones?: boolean;
  timezone?: Timezone;
}) => {
  const [frequency, setFrequency] = useState<CronFrequency>(initialState?.frequency || CronFrequency.Weekly);
  const [selectedDays, setSelectedDays] = useState<CronDayOfWeek[]>(
    initialState?.selectedDays || [CronDayOfWeek.Monday]
  );
  const [selectedHour, setSelectedHour] = useState(initialState?.selectedHour || '09');
  const [selectedMinute, setSelectedMinute] = useState(initialState?.selectedMinute || '00');
  const [meridiem, setMeridiem] = useState(initialState?.meridiem || AmPm.AM);
  const [ignoreTimezones, setIgnoreTimezones] = useState(initialState?.ignoreTimezones || false);
  const [timezone, setTimezone] = useState<Timezone>(initialState?.timezone || Timezone.UTC);
  const [cronString, setCronString] = useState('');

  const [parseError, setParseError] = useState('');
  const [parseSuccess, setParseSuccess] = useState('');

  useEffect(() => {
    let hour24 = parseInt(selectedHour, 10);
    let minute = parseInt(selectedMinute, 10);

    // Convert 12-hour to 24-hour format first
    if (meridiem === AmPm.PM && hour24 !== 12) {
      hour24 += 12;
    } else if (meridiem === AmPm.AM && hour24 === 12) {
      hour24 = 0;
    }

    // If EST/EDT timezone, convert to UTC for cron storage
    if (timezone === Timezone.EST || timezone === Timezone.EDT) {
      const offset = getTimezoneOffset(timezone);
      hour24 += offset;
      if (hour24 >= 24) {
        hour24 -= 24;
      }
    }

    let newCronString = '';
    const minutePart = String(minute).padStart(2, '0');
    const hourPart = String(hour24).padStart(2, '0');

    const dayMap: Record<CronDayOfWeek, number> = {
      [CronDayOfWeek.Sunday]: 0,
      [CronDayOfWeek.Monday]: 1,
      [CronDayOfWeek.Tuesday]: 2,
      [CronDayOfWeek.Wednesday]: 3,
      [CronDayOfWeek.Thursday]: 4,
      [CronDayOfWeek.Friday]: 5,
      [CronDayOfWeek.Saturday]: 6,
    };

    switch (frequency) {
      case CronFrequency.Weekly:
        const dayNumbers = selectedDays
          .map((day) => dayMap[day])
          .sort((a, b) => a - b)
          .join(',');
        newCronString = `${minutePart} ${hourPart} * * ${dayNumbers}`;
        break;
      case CronFrequency.Monthly:
        newCronString = `${minutePart} ${hourPart} 1 * *`;
        break;
      case CronFrequency.Quarterly:
        newCronString = `${minutePart} ${hourPart} 1 1,4,7,10 *`;
        break;
      case CronFrequency.Yearly:
        newCronString = `${minutePart} ${hourPart} 1 1 *`;
        break;
      default:
        newCronString = '';
    }
    setCronString(newCronString);
  }, [frequency, selectedDays, selectedHour, selectedMinute, meridiem, ignoreTimezones, timezone]);

  const parseCronString = (inputString: string) => {
    setParseError('');
    setParseSuccess('');
    try {
      const parts = inputString.trim().split(/\s+/);
      if (parts.length !== 5) {
        throw new Error('Invalid cron string format. Must have 5 parts.');
      }

      const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

      const inputHour = parseInt(hour, 10);
      let finalHour = inputHour;

      // If EST/EDT timezone, convert from UTC to local time for display
      if (timezone === Timezone.EST || timezone === Timezone.EDT) {
        const offset = getTimezoneOffset(timezone);
        finalHour = inputHour - offset;
        if (finalHour < 0) {
          finalHour += 24;
        }
      }

      let parsedMeridiem = AmPm.AM;
      let parsedHour = String(finalHour);

      if (finalHour >= 12) {
        parsedMeridiem = AmPm.PM;
        if (finalHour > 12) {
          parsedHour = String(finalHour - 12);
        }
      } else if (finalHour === 0) {
        parsedHour = '12';
      }

      const dayMap: Record<number, CronDayOfWeek> = {
        0: CronDayOfWeek.Sunday,
        1: CronDayOfWeek.Monday,
        2: CronDayOfWeek.Tuesday,
        3: CronDayOfWeek.Wednesday,
        4: CronDayOfWeek.Thursday,
        5: CronDayOfWeek.Friday,
        6: CronDayOfWeek.Saturday,
      };
      let newFrequency: CronFrequency | null = null;
      let newDays: CronDayOfWeek[] = [];

      if (dayOfMonth === '*' && month === '*' && dayOfWeek !== '*') {
        newFrequency = CronFrequency.Weekly;
        const dayNumbers = dayOfWeek.split(',').map(Number);
        newDays = dayNumbers.map((num) => dayMap[num]).filter(Boolean);
        if (newDays.length === 0) {
          throw new Error('Invalid day of week in cron string.');
        }
      } else if (dayOfMonth === '1' && month === '*' && dayOfWeek === '*') {
        newFrequency = CronFrequency.Monthly;
      } else if (dayOfMonth === '1' && month === '1,4,7,10' && dayOfWeek === '*') {
        newFrequency = CronFrequency.Quarterly;
      } else if (dayOfMonth === '1' && month === '1' && dayOfWeek === '*') {
        newFrequency = CronFrequency.Yearly;
      } else {
        throw new Error('Unsupported cron format. Please use one of the supported frequencies.');
      }

      // Update state
      if (newFrequency) {
        setFrequency(newFrequency);
        setSelectedDays(newDays.length > 0 ? newDays : [CronDayOfWeek.Monday]);
        setSelectedHour(parsedHour.padStart(2, '0'));
        setSelectedMinute(minute.padStart(2, '0'));
        setMeridiem(parsedMeridiem as AmPm);
        setParseSuccess('Cron string parsed successfully!');
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setParseError(e?.message);
    }
  };

  // Function to toggle day selection for Weekly frequency
  const toggleSelectedDay = useCallback((day: CronDayOfWeek) => {
    setSelectedDays((prevDays) => {
      const isSelected = prevDays.includes(day);
      if (isSelected) {
        if (prevDays.length > 1) {
          return prevDays.filter((d) => d !== day);
        }
        return prevDays;
      } else {
        return [...prevDays, day];
      }
    });
  }, []);
  const setSelectedDay = useCallback((day: CronDayOfWeek) => {
    setSelectedDays([day]);
  }, []);

  return {
    frequency,
    setFrequency,
    selectedDays,
    setSelectedDay,
    toggleSelectedDay,
    selectedHour,
    setSelectedHour,
    selectedMinute,
    setSelectedMinute,
    meridiem,
    setMeridiem,
    ignoreTimezones,
    setIgnoreTimezones,
    timezone,
    setTimezone,
    cronString,

    parseCronString,
    parseError,
    parseSuccess,
    detectEasternTimezone,
  };
};
export default useCronBuilder;
